// FW-2A: the one client-side preview of a sales line (gross / discount / net), shared by every sales line editor.
// The server (SalesLineMath) is authoritative and recomputes everything on save; this only drives the live totals.
// A percentage wins over an amount, as on the server.

const r4 = (n: number) => Math.round((n + Number.EPSILON) * 10000) / 10000;
const fin = (n: number | null | undefined) => (typeof n === 'number' && Number.isFinite(n) ? n : 0);

export interface LineDiscountInput {
  quantity: number;
  unitPrice: number;
  discountPct?: number | null;
  discountAmount?: number | null;
}

export interface LinePreview {
  gross: number;
  discount: number;
  net: number;
}

export function previewLine(l: LineDiscountInput): LinePreview {
  const gross = r4(fin(l.quantity) * fin(l.unitPrice));
  let discount = 0;
  if (typeof l.discountPct === 'number' && Number.isFinite(l.discountPct)) {
    discount = r4((gross * l.discountPct) / 100);
  } else if (typeof l.discountAmount === 'number' && Number.isFinite(l.discountAmount)) {
    discount = r4(l.discountAmount);
  }
  discount = Math.min(Math.max(discount, 0), gross);
  return { gross, discount, net: r4(gross - discount) };
}

/** Whether the discount entered on a line is valid (percentage 0-100, amount within the gross). */
export function discountError(l: LineDiscountInput): string | null {
  const gross = r4(fin(l.quantity) * fin(l.unitPrice));
  if (typeof l.discountPct === 'number' && Number.isFinite(l.discountPct)) {
    return l.discountPct < 0 || l.discountPct > 100 ? 'Discount % must be between 0 and 100' : null;
  }
  if (typeof l.discountAmount === 'number' && Number.isFinite(l.discountAmount)) {
    if (l.discountAmount < 0) return 'Discount cannot be negative';
    if (l.discountAmount > gross) return 'Discount cannot exceed the line amount';
  }
  return null;
}

/** Row patch when the user edits one of the two discount fields: the other is cleared. NaN/empty clears both. */
export function discountPatch(field: 'pct' | 'amount', raw: string): { discountPct: number | null; discountAmount: number | null } {
  if (raw === '') return { discountPct: null, discountAmount: null };
  const v = Number(raw);
  return field === 'pct' ? { discountPct: v, discountAmount: null } : { discountPct: null, discountAmount: v };
}

/** The amount shown in the amount box: what the user typed, else the amount a typed percentage works out to. */
export function shownDiscountAmount(l: LineDiscountInput): string {
  if (typeof l.discountPct === 'number' && Number.isFinite(l.discountPct)) return String(previewLine(l).discount);
  return typeof l.discountAmount === 'number' && Number.isFinite(l.discountAmount) ? String(l.discountAmount) : '';
}

export const hasDiscount = (lines: { discountAmount?: number | null }[]): boolean =>
  lines.some(l => Number(l.discountAmount ?? 0) > 0);
